/**
 * Catalog namespace loader (M9).
 * Core namespaces load eagerly; route-specific namespaces can load on demand.
 */
import { FALLBACK_LOCALE } from "./localeResolution.js";

export const CATALOG_NAMESPACES = [
  "common",
  "automotive",
  "realEstate",
  "forms",
  "validation",
  "preferences",
  "studio",
  "admin",
  "clientView",
];

/** Always bundled — required for forms and shell UI. */
export const EAGER_NAMESPACES = ["common", "forms", "validation"];

/** Loaded on demand when ensureNamespace() is called. */
export const LAZY_NAMESPACES = CATALOG_NAMESPACES.filter((ns) => !EAGER_NAMESPACES.includes(ns));

const namespaceCache = new Map();

function cacheKey(locale, namespace) {
  return `${locale}:${namespace}`;
}

/**
 * Dynamic import map for lazy namespaces. Missing files resolve to null (fallback applies).
 */
async function importNamespaceJson(locale, namespace) {
  try {
    const mod = await import(`./catalogs/${locale}/${namespace}.json`);
    return mod.default || mod;
  } catch {
    return null;
  }
}

export async function loadNamespace(locale, namespace) {
  const key = cacheKey(locale, namespace);
  if (namespaceCache.has(key)) {
    return namespaceCache.get(key);
  }
  const data = await importNamespaceJson(locale, namespace);
  namespaceCache.set(key, data);
  return data;
}

export async function ensureNamespaces(locale, namespaces = []) {
  const loaded = {};
  await Promise.all(
    namespaces.map(async (namespace) => {
      const data = await loadNamespace(locale, namespace);
      if (data) {
        loaded[namespace] = data;
      }
    })
  );
  return loaded;
}

export function mergeNamespacePatch(catalog, patch) {
  return {
    ...catalog,
    ...Object.fromEntries(
      Object.entries(patch).map(([namespace, values]) => [
        namespace,
        { ...(catalog[namespace] || {}), ...values },
      ])
    ),
  };
}

export function clearNamespaceCacheForTests() {
  namespaceCache.clear();
}

export function preloadFallbackNamespaces(fallbackLocale = FALLBACK_LOCALE) {
  return ensureNamespaces(fallbackLocale, LAZY_NAMESPACES);
}
